import React, { useEffect, useState } from 'react';
import { GradientText, Spacer } from 'src/common';
import { useTheme } from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import { MOTION_TRANSITION } from 'src/config';
import { loadSection, saveSection } from './util';
import { useLocation } from 'react-router-dom';
import * as S from './ExpandSection.styles';

interface ExpandSectionProps {
  title: string;
  children: React.ReactNode;
  onChange?: (isOpen: boolean) => void;
  endComponent?: React.ReactNode;
  sectionId: string;
  defaultOpen?: boolean;
}

const ExpandSection: React.FC<ExpandSectionProps> = ({
  title,
  children,
  onChange,
  endComponent,
  sectionId = 'default',
  defaultOpen = true,
}) => {
  const colors = useTheme().colors;
  const init = !!(loadSection(sectionId) === null ? defaultOpen : loadSection(sectionId));
  const [isOpen, setIsOpen] = useState(init);
  const location = useLocation();

  useEffect(() => {
    onChange?.(isOpen);
  }, [isOpen]);

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (!hash) return;

    const titleId = title.toLowerCase().replace(/\s+/g, '-');

    if (hash === sectionId || hash === titleId) {
      if (!isOpen) {
        setIsOpen(true);
        saveSection({ sectionId, isOpen: true });
      }

      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          const mainContainer = document.getElementById('main');
          if (mainContainer) {
            const elementTop = element.offsetTop;
            mainContainer.scrollTo({ top: elementTop - 20, behavior: 'smooth' });
          } else {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }, 0);
    }
  }, [location.hash, sectionId, title, isOpen]);

  return (
    <S.Div data-cy={`es:${sectionId}`} id={sectionId}>
      <div
        className="head"
        onClick={() => {
          const newValue = !isOpen;
          setIsOpen(newValue);
          saveSection({ sectionId, isOpen: newValue });
        }}
        data-cy={`es:${sectionId}:handle`}
      >
        <div className="head__handle">
          {isOpen ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="2" viewBox="0 0 14 2" fill="none">
              <path
                d="M13.4298 1.83982L13.4162 0.416223L7.77232 0.362483L6.34872 0.348928L0.704811 0.295189L0.718366 1.71879L6.36227 1.77253L7.78587 1.78608L13.4298 1.83982Z"
                fill={colors.cflowerBlue}
              />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M13.4298 7.59275L13.4162 6.16915L7.77232 6.11541L7.71858 0.471506L6.29498 0.457951L6.34872 6.10186L0.704811 6.04812L0.718366 7.47172L6.36227 7.52546L6.41601 13.1694L7.83961 13.1829L7.78587 7.53901L13.4298 7.59275Z"
                fill={colors.cflowerBlue}
              />
            </svg>
          )}

          <GradientText>{title}</GradientText>
        </div>
        <div className="head__extra" onClick={(e) => e.stopPropagation()}>
          <AnimatePresence>
            {endComponent && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.0 }}
                exit={{ opacity: 0, y: -5 }}
              >
                {endComponent}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <motion.div
        className="expand"
        initial={false}
        animate={{ height: isOpen ? 'auto' : 0, y: isOpen ? 0 : -10 }}
        transition={{ ...MOTION_TRANSITION, delay: 0.1 }}
      >
        <Spacer $px={2} />
        {children}
        <Spacer $px={30} />
      </motion.div>
    </S.Div>
  );
};

export default ExpandSection;
